import React, { useState, useContext } from "react";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast.jsx";
import { Helmet } from "react-helmet-async";
import Api from "../utils/api";
import forgotPasswordTranslations from "../locales/forgotPasswordTranslations";
import { LanguageContext } from "../locales/LanguageContext";
import {
  Box,
  Card,
  Typography,
  InputAdornment,
  TextField,
  Button,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";

const ForgotPassword = () => {
  const { lang } = useContext(LanguageContext);
  const t = forgotPasswordTranslations[lang] || {};
  const [email, setEmail] = useState("");
  const [toast, showToast] = useToast();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await Api.post("auth/forgot-password", { email });
      showToast(t.success, "success");
    } catch (err) {
      showToast(err.message || t.error, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GlobalToast toast={toast} />
      <Helmet>
        <title>{t.pageTitle} | AKAAL KIDS</title>
      </Helmet>
      {/* Animated gradient header */}
      <Box
        sx={{
          width: "100%",
          background:
            "linear-gradient(270deg, #f6815e, #f6b93b, #fffbe6, #f6815e)",
          backgroundSize: "600% 600%",
          color: "#fff",
          py: 5,
          textAlign: "center",
          mb: 4,
          boxShadow: "0 4px 24px #f6815e22",
          position: "relative",
          overflow: "hidden",
          animation: "gradientBG 12s ease infinite",
          "@keyframes gradientBG": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <Typography
          variant="h2"
          fontWeight={900}
          sx={{ letterSpacing: 1, mb: 1, textShadow: "0 2px 12px #f6815e55" }}
        >
          {t.heroTitle}
        </Typography>
        <Typography variant="h5" fontWeight={500} sx={{ opacity: 0.96 }}>
          <Lock sx={{ verticalAlign: "middle", mr: 1 }} />
          {t.heroDesc}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontStyle: "italic", opacity: 0.9 }}
        >
          {"Forgot your password? No worries, we’ll help you get back in!"}
        </Typography>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
      >
        <Card
          sx={{
            p: 4,
            borderRadius: 4,
            boxShadow: 12,
            minWidth: 340,
            maxWidth: 400,
            width: "100%",
            transition: "transform 0.25s cubic-bezier(.4,2,.6,1)",
            "&:hover": { transform: "scale(1.025)", boxShadow: 20 },
          }}
        >
          <form onSubmit={handleSubmit}>
            <TextField
              type="email"
              label={t.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: "#f6815e" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 3,
                mb: 2,
                fontWeight: 700,
                background: "linear-gradient(90deg,#f6815e,#f6b93b)",
                boxShadow: 2,
                "&:hover": {
                  background: "linear-gradient(90deg,#f6b93b,#f6815e)",
                },
              }}
              disabled={loading}
            >
              {loading ? t.sending : t.sendBtn}
            </Button>
          </form>
        </Card>
      </Box>
    </>
  );
};

export default ForgotPassword;
